import type { Portal } from '@/features/auth/types'
import i18n, { intlLocale } from '@/i18n'

const t = (key: string, options?: Record<string, unknown>) => i18n.t(key, { ns: 'messaging', ...options })

export function messagesBase(portal: Portal | null): string {
  return portal === 'family' ? '/portal/messages' : portal === 'staff' ? '/staff/messages' : '/admin/messages'
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

/** 14:05 */
export function timeOf(iso: string): string {
  return new Intl.DateTimeFormat(intlLocale(), { hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
}

/** List label: the time today, "Yesterday", the weekday this week, else the date. */
export function shortWhen(iso: string, now = new Date()): string {
  const d = new Date(iso)
  if (sameDay(d, now)) return timeOf(iso)
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(d, yesterday)) return t('Yesterday')
  if (now.getTime() - d.getTime() < 6 * 86_400_000) {
    return new Intl.DateTimeFormat(intlLocale(), { weekday: 'short' }).format(d)
  }
  return new Intl.DateTimeFormat(intlLocale(), { day: 'numeric', month: 'short' }).format(d)
}

/** Separator between days in a thread. */
export function dayLabel(iso: string, now = new Date()): string {
  const d = new Date(iso)
  if (sameDay(d, now)) return t('Today')
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(d, yesterday)) return t('Yesterday')
  return new Intl.DateTimeFormat(intlLocale(), { weekday: 'long', day: 'numeric', month: 'long' }).format(d)
}

export function isSameDay(a: string, b: string): boolean {
  return sameDay(new Date(a), new Date(b))
}

/** 1:05:09 or 4:07 */
export function duration(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Administration',
  admin: 'Administration',
  teacher: 'Teacher',
  accountant: 'Accountant',
  librarian: 'Librarian',
  receptionist: 'Reception',
  student: 'Student',
  parent: 'Parent / Guardian',
}

export function roleLabel(role: string): string {
  return t(ROLE_LABELS[role] ?? role)
}
